import React, { Dispatch, useMemo } from 'react';
import { Box, Divider, IconButton, Stack } from '@mui/material';
import { Edit as EditIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { ScoutCalendarState, ScoutCalendarAction } from '../../types';
import { classes } from './styles';
import GroupedAvatars from './GroupedAvatars';
import DateDisplay from './DateDisplay';
import { theme } from '../../../ThemeContext/ThemeObject';
import { CandidateGroupedAvatars } from '../../helper';
import { formatUserPermissions } from '../../../NewUI/Components/Utilities/formatUserPermissions';
import { useFeatureFlag } from '../../../FeatureFlags';
import GroupedChips from './GroupedChips';
import VideoLinkSection from '../../VideoLinkSection';
import VideoConferenceManager from '../../VideoConferenceManager';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { userTimeZone } from '../../../NewUI/utils/formatToUserTimeZone';

dayjs.extend(utc);
dayjs.extend(timezone);

export default function ReviewStep({
  ScoutCalendarState,
  dispatch,
  setTab,
  eventData,
  onEventUpdate
}: {
  ScoutCalendarState: ScoutCalendarState;
  dispatch: Dispatch<ScoutCalendarAction>;
  setTab?: (tab: number) => void;
  eventData?: { video_conference_url?: string; video_conference_provider?: string };
  onEventUpdate?: (updatedEvent: any) => void;
}) {
  const newEventManagementEnabled = useFeatureFlag('newEventManagementEnabled');
  const hasVideoConferencePermission = useFeatureFlag('videoConferenceIntegrationEnabled');
  const { createEventObject, createMultipleEvents, showViewEventModal } = ScoutCalendarState;
  const permissions = formatUserPermissions();
  const { recruiters, dates, owner, candidates, jobs } = createEventObject;
  const hasDescription = Boolean(createEventObject.eventDescription);
  const descriptionText = hasDescription ? createEventObject.eventDescription : '-';

  const [videoConferenceUrl, setVideoConferenceUrl] = React.useState(
    eventData?.video_conference_url
  );
  const [videoProvider, setVideoProvider] = React.useState(eventData?.video_conference_provider);

  const handleVideoConferenceUpdate = (data: {
    video_conference_url?: string;
    video_conference_provider?: string;
  }) => {
    setVideoConferenceUrl(data.video_conference_url);
    setVideoProvider(data.video_conference_provider);
  };

  const memorisedDescriptionText = useMemo(
    () => (
      <>
        <Stack sx={{ color: '#939393' }}>Description</Stack>
        <Stack>{descriptionText}</Stack>
      </>
    ),
    [descriptionText]
  );

  return (
    <Box flexGrow={1}>
      {!showViewEventModal && <Box sx={classes.progressHeader}>Final step! Is this correct?</Box>}
      <Stack
        sx={{
          maxHeight: showViewEventModal ? 'calc(100vh - 200px)' : '455px',
          minHeight: showViewEventModal ? '400px' : '455px',
          rowGap: '20px',
          overflow: showViewEventModal ? 'visible' : 'auto',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        <Stack>
          <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', color: '#939393' }}>
            Event type
            {permissions?.['Events']?.['Create / Edit Events'] && (
              <EditIcon
                id="edit-step-one-button"
                sx={classes.editIcon}
                onClick={() => {
                  if (createEventObject.eventId) {
                    dispatch({ type: 'SET_SHOW_VIEW_EVENT_MODAL', payload: false });
                    dispatch({ type: 'SET_SHOW_CREATE_EVENT_MODAL', payload: true });
                  }
                  dispatch({
                    type: 'SET_CREATE_EVENT_OBJECT',
                    payload: { ...createEventObject, step: 1 }
                  });
                }}
              />
            )}
          </Stack>
          <Stack>{createEventObject.eventType?.name}</Stack>
        </Stack>
        <Stack sx={{ flexDirection: 'row' }}>
          <Stack sx={{ width: '100%' }}>
            <Stack sx={{ color: '#939393' }}>Date</Stack>
            <DateDisplay {...{ dates }} />
          </Stack>
          <Stack sx={{ width: '100%' }}>
            <Stack sx={{ color: '#939393' }}>Time</Stack>
            <Stack>{dayjs(createEventObject.eventDate).tz(userTimeZone).format('LT')}</Stack>
          </Stack>
          <Stack sx={{ width: '100%' }}>
            <Stack sx={{ color: '#939393' }}>Duration</Stack>
            <Stack>{createEventObject.duration} minutes</Stack>
          </Stack>
        </Stack>
        {!owner && (
          <Stack sx={{ flexDirection: 'row' }}>
            <Stack sx={{ width: '100%' }}>
              <Stack sx={{ color: '#939393' }}>{`Max. candidates (per session)`}</Stack>
              <Stack>{createEventObject.maxCandidates}</Stack>
            </Stack>
            {createMultipleEvents && (
              <>
                <Stack sx={{ width: '100%' }}>
                  <Stack sx={{ color: '#939393' }}>{`Sessions (in the day)`}</Stack>
                  <Stack>{createEventObject.sessions}</Stack>
                </Stack>
                <Stack sx={{ width: '100%' }}>
                  <Stack sx={{ color: '#939393' }}>{`Break (in between sessions)`}</Stack>
                  <Stack>{createEventObject.breaks} minutes</Stack>
                </Stack>
              </>
            )}
          </Stack>
        )}
        <Stack sx={{ flexDirection: 'row' }}>
          {owner && (
            <Stack sx={{ width: '33%' }} gap={0.5}>
              <Stack sx={{ color: '#939393' }}>Created by</Stack>
              <GroupedAvatars users={[{ id: 0, name: owner }]} />
            </Stack>
          )}
          <Stack sx={{ width: '33%' }} gap={0.5}>
            <Stack sx={{ color: '#939393' }}>Recruiters</Stack>
            <GroupedAvatars
              users={recruiters.map((r) => ({ id: r.id, name: r.name, state: r.state }))}
            />
          </Stack>
          {newEventManagementEnabled && (
            <Stack sx={{ width: '33%', paddingRight: '8px' }} gap={0.5}>
              <Stack sx={{ color: '#939393' }}>Jobs</Stack>
              <GroupedChips jobs={jobs} />
            </Stack>
          )}
          {showViewEventModal && (
            <Stack sx={{ width: '33%' }} gap={0.5}>
              <Stack sx={{ color: '#939393' }}>
                Candidates ({candidates.length}/{createEventObject.maxCandidates} booked)
              </Stack>
              <Stack direction={'row'} gap={0.5}>
                {candidates.length ? (
                  <>
                    <CandidateGroupedAvatars candidates={candidates} />
                    <IconButton
                      onClick={() => setTab?.(2)}
                      sx={{ color: theme.palette.common.darkGrey, width: 'min-content' }}
                      size="small"
                      id={'candidates-button-details-tab'}
                    >
                      <ChevronRightIcon />
                    </IconButton>
                  </>
                ) : (
                  <Stack sx={classes.noCandidatesAssigned}>No Candidates Assigned</Stack>
                )}
              </Stack>
            </Stack>
          )}
        </Stack>
        <Divider />
        <Stack>
          <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', color: '#939393' }}>
            Event name
            {permissions?.['Events']?.['Create / Edit Events'] && (
              <EditIcon
                id="edit-step-two-button"
                sx={classes.editIcon}
                onClick={() => {
                  if (createEventObject.eventId) {
                    dispatch({ type: 'SET_SHOW_VIEW_EVENT_MODAL', payload: false });
                    dispatch({ type: 'SET_SHOW_CREATE_EVENT_MODAL', payload: true });
                  }
                  dispatch({
                    type: 'SET_CREATE_EVENT_OBJECT',
                    payload: { ...createEventObject, step: 2 }
                  });
                }}
              />
            )}
          </Stack>
          <Stack sx={{ wordWrap: 'break-word' }}>{createEventObject.eventName}</Stack>
        </Stack>
        <Stack sx={{ overflowWrap: 'anywhere' }}>
          {showViewEventModal && hasDescription ? memorisedDescriptionText : null}
          {!showViewEventModal && memorisedDescriptionText}
        </Stack>

        {hasVideoConferencePermission && showViewEventModal && createEventObject.eventId && (
          <VideoConferenceManager
            eventId={createEventObject.eventId}
            hasVideoLink={Boolean(videoConferenceUrl)}
            videoUrl={videoConferenceUrl}
            videoProvider={videoProvider}
            onVideoConferenceUpdate={handleVideoConferenceUpdate}
            onEventUpdate={onEventUpdate}
            isEditable={permissions?.['Events']?.['Create / Edit Events']}
          />
        )}
        {hasVideoConferencePermission && !showViewEventModal && (
          <VideoLinkSection videoUrl={videoConferenceUrl || ''} videoProvider={videoProvider} />
        )}
      </Stack>
    </Box>
  );
}
